import React, { FC } from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';
import { SubmissionErrors } from 'final-form';

import {
  DropdownWrapper,
  TextAreaWrapper,
  TextInputWrapper,
  NumberInputWrapper,
  SwitchInputWrapper,
} from 'components/forms';
import { Accordion } from 'components';
import { ICountry } from '../store/actions';
import { Heading, Note } from 'components/ui';
import { IAppCountryForm } from './AppPricing';
import { currencySymbols, floatNotNegativeNumberRegex, priceWithMax2DecimalsRegex, t } from 'utils';

import styles from './AppPricing.module.scss';

export interface IAppPricingCountryForm {
  i: number;
  plan?: any;
  firstPlan?: any;
  hasError?: boolean;
  planIndex?: number;
  copyPrices?: boolean;
  form?: any;
  countries: ICountry[];
  isFormDisabled: boolean;
  isPricesDisabled: boolean;
  allowedCountry: IAppCountryForm;
  submitErrors?: SubmissionErrors;
  errors?: { [key: string]: any };
  allowedCountries: IAppCountryForm[];
  payPerUsageType?: 'fixed' | 'variable';
  type: 'onetime' | 'payPerUsage' | 'membership';
  dirtyFieldsSinceLastSubmit?: { [key: string]: boolean };
}

const AppPricingCountryForm: FC<IAppPricingCountryForm> = (props) => {
  const {
    i,
    form,
    type,
    // plan,
    errors,
    planIndex,
    firstPlan,
    countries,
    copyPrices,
    submitErrors,
    isFormDisabled,
    allowedCountry,
    payPerUsageType,
    isPricesDisabled,
    allowedCountries,
    dirtyFieldsSinceLastSubmit,
  } = props;
  const isPayPerUsage = type === 'payPerUsage';
  const isMembership = type === 'membership';
  const isOnetime = type === 'onetime';
  const selectedCountry = countries?.find(
    (country: ICountry) => country.code === allowedCountry.country
  );
  const countryCurrency = selectedCountry?.versionOnePrices?.overwriteCurrencyCode ?? 'EUR';
  const countriesToPrefillFrom = allowedCountries
    .filter((country: IAppCountryForm) => country.country !== allowedCountry.country)
    .map((country: IAppCountryForm) => {
      const countryWithoutLabel = countries?.find(
        (countryWithoutLabel: ICountry) => countryWithoutLabel.code === country.country
      );
      const indexOfCountryInAllowedCountries = allowedCountries.findIndex(
        (allowedCountry: IAppCountryForm) => countryWithoutLabel?.code === allowedCountry.country
      );
      if (indexOfCountryInAllowedCountries >= 0) {
        const countryIsNotPrefilledAlready =
          allowedCountries[indexOfCountryInAllowedCountries]?.prefillCountryIndex === 'none';
        return {
          ...countryWithoutLabel,
          disabled: !countryIsNotPrefilledAlready, // disable prefilling from already prefilled country - too many forms
          value: indexOfCountryInAllowedCountries.toString(), // index for input names
        };
      }
      return countryWithoutLabel ?? country;
    });
  const prefillCountryOptions = [
    {
      label: 'pricing.prefill.notSelected',
      value: 'none',
    },
    ...countriesToPrefillFrom,
  ];
  const countryIndexToPrefillFrom = allowedCountry.prefillCountryIndex;
  const countryIsPrefilled = countryIndexToPrefillFrom !== 'none'; // detect whether prefill dropdown is filled to disable inputs
  const countryIndex =
    parseFloat(countryIndexToPrefillFrom) >= 0 ? parseFloat(countryIndexToPrefillFrom) : i;
  const currencySymbol = allowedCountry.currencyCode
    ? currencySymbols[allowedCountry.currencyCode] ?? allowedCountry.currencyCode
    : allowedCountry.currencyCode;
  // value of field is different - it comes from plan in membership
  const shouldRenderOnetimeFeeInput = isMembership
    ? firstPlan?.onetimeFee
    : allowedCountries[countryIndex].onetimeFee;
  const submissionErrors = isMembership
    ? submitErrors?.plans?.[planIndex ?? 0]
    : submitErrors?.allowedCountries?.[countryIndex];
  // const submissionErrors = submitErrors?.allowedCountries?.[countryIndex];
  const formErrors = errors?.allowedCountries?.[countryIndex];
  const allErrorsFields = [
    ...Object.keys(submissionErrors ?? {}),
    ...Object.keys(formErrors ?? {}),
  ].reduce((errors: any, errorField: string) => {
    // if the error field is not modified after last submitting, put it here
    if (
      !dirtyFieldsSinceLastSubmit?.[`allowedCountries[${countryIndex}].${errorField}`] ||
      errorField === 'trialUnits' // bc field is not part of countries. Think of refactor in future
    )
      return [...errors, errorField];
    return errors;
  }, []);

  return (
    <Accordion
      key={allowedCountry.country}
      title={
        selectedCountry
          ? t(selectedCountry.translationKey ?? selectedCountry.label)
          : allowedCountry.country
      }
      extraText={
        allErrorsFields.length > 0 && (
          <span className={styles['accordion-error-text']}>
            {t('pricing.errors.general', [
              { key: 'ERROR', value: 'pricing.errors.missingData', translateReplace: true },
            ])}
          </span>
        )
      }
      className={classNames('mb-4', styles['country-accordion'], {
        [styles['accordion-error']]: allErrorsFields.length > 0,
      })}
    >
      {countriesToPrefillFrom.length > 0 && (
        <>
          <Heading className={classNames(styles.heading, 'mb-4')} noTopPadding>
            {t('pricing.payPerUsage.prefill.title')}
          </Heading>
          <Field
            name={`allowedCountries[${i}].prefillCountryIndex`}
            component={DropdownWrapper}
            label='pricing.payPerUsage.prefill.label'
            placeholder='pricing.payPerUsage.prefill.placeholder'
            options={prefillCountryOptions}
            className='mb-3'
            disabled={isFormDisabled}
            isShowSelected
          />
        </>
      )}
      {isPayPerUsage && (
        <>
          <Heading className={styles.heading} noTopPadding={countriesToPrefillFrom.length === 0}>
            {t('pricing.payPerUsage.naming.title')}
          </Heading>
          <Field
            name={`allowedCountries[${countryIndex}].unitDescription`}
            error={
              !dirtyFieldsSinceLastSubmit?.[`allowedCountries[${countryIndex}].unitDescription`] &&
              submissionErrors?.unitDescription
            }
            component={TextInputWrapper}
            label='pricing.payPerUsage.entity.label'
            placeholder='pricing.payPerUsage.entity.placeholder'
            validate={(value = '', allValues, fieldState) => {
              const { visited } = fieldState ?? {};
              if (!visited) return undefined;
              return value?.trim().length < 1
                ? t('errors.entityLength', [{ key: 'LIMIT', value: '1' }])
                : undefined;
            }}
            className='mb-1'
            disabled={isFormDisabled || countryIsPrefilled}
            required
          />
          <Note className='mt-1 mb-2'>{t('pricing.payPerUsage.entity.hint')}</Note>
          <Field
            name={`allowedCountries[${countryIndex}].unitDescriptionPlural`}
            error={
              !dirtyFieldsSinceLastSubmit?.[
                `allowedCountries[${countryIndex}].unitDescriptionPlural`
              ] && submissionErrors?.unitDescriptionPlural
            }
            component={TextInputWrapper}
            label='pricing.payPerUsage.entityPlural.label'
            placeholder='pricing.payPerUsage.entityPlural.placeholder'
            validate={(value = '', allValues, fieldState) => {
              const { visited } = fieldState ?? {};
              if (!visited) return undefined;
              return value?.trim().length < 1
                ? t('errors.entityLength', [{ key: 'LIMIT', value: '1' }])
                : undefined;
            }}
            className='mb-1'
            disabled={isFormDisabled || countryIsPrefilled}
            required
          />
          <Note className='mt-1 mb-2'>{t('pricing.payPerUsage.entityPlural.hint')}</Note>
        </>
      )}
      {isPayPerUsage && (
        <>
          <Heading className={styles.heading}>
            {t('pricing.payPerUsage.pricingBracket.title')}
          </Heading>
          {payPerUsageType === 'fixed' && (
            <Field
              name={
                copyPrices
                  ? `copyPricesUnitPrice${countryCurrency}`
                  : `allowedCountries[${countryIndex}].plans[0].usageUnitPrice`
              }
              error={
                !dirtyFieldsSinceLastSubmit?.[
                  `allowedCountries[${countryIndex}].plans[0].usageUnitPrice`
                ] && submissionErrors?.[`plans[0].usageUnitPrice`]
              }
              label='pricing.payPerUsage.price.label'
              placeholder='pricing.payPerUsage.price.placeholder'
              pattern={priceWithMax2DecimalsRegex}
              validate={(value = '', allValues, fieldState) => {
                const { visited } = fieldState ?? {};
                if (!visited) return undefined;
                const formattedValue = `${value}`;
                let isPriceValid = false;
                if (formattedValue.match(priceWithMax2DecimalsRegex) && formattedValue !== '0.00') {
                  isPriceValid = true;
                }
                return isPriceValid ? undefined : t('errors.price.invalid');
              }}
              changeValue={form?.change}
              currencySymbol={currencySymbol}
              component={NumberInputWrapper}
              disabled={isPricesDisabled || isFormDisabled || countryIsPrefilled}
              required
              initialValue=''
            />
          )}
          {payPerUsageType === 'variable' && (
            <div
              className={classNames(styles['ta-copy-prices'], styles['ta-copy-prices__country'])}
            >
              <Field
                name={
                  copyPrices
                    ? `copyPricesMin${countryCurrency}`
                    : `allowedCountries[${countryIndex}].plans[0].usageMinUnitPrice`
                }
                error={
                  (!dirtyFieldsSinceLastSubmit?.[
                    `allowedCountries[${countryIndex}].plans[0].usageMinUnitPrice`
                  ] &&
                    submissionErrors?.[`plans[0].usageMinUnitPrice`]) ||
                  errors?.allowedCountries?.[countryIndex]?.plans?.[0]?.usageMinUnitPrice
                }
                changeValue={form?.change}
                label='pricing.payPerUsage.minPrice.label'
                placeholder='pricing.payPerUsage.minPrice.placeholder'
                validateFields={[
                  copyPrices
                    ? `copyPricesMax${countryCurrency}`
                    : `allowedCountries[${countryIndex}].plans[0].usageMaxUnitPrice`,
                ]}
                validate={(value = '', allValues: { [key: string]: any }, fieldState) => {
                  const { visited } = fieldState ?? {};
                  if (!visited) return undefined;
                  const { allowedCountries, copyPrices } = allValues;
                  const selectedAllowedCountry = allowedCountries[countryIndex];
                  const payPerUsageMaxPrice: number = copyPrices
                    ? allValues[`copyPricesMax${countryCurrency}`]
                    : selectedAllowedCountry?.plans?.[0]?.usageMaxUnitPrice;
                  const formattedValue = `${value}`;
                  const numberFormattedValue = parseFloat(formattedValue);
                  const numberPayPerUsageMaxPrice = parseFloat(`${payPerUsageMaxPrice ?? 0}`);
                  const isBiggerThanMaxPrice = numberFormattedValue > numberPayPerUsageMaxPrice;
                  const isSameAsMaxPrice = numberFormattedValue === numberPayPerUsageMaxPrice;
                  let error; // no error
                  if (
                    !formattedValue.match(priceWithMax2DecimalsRegex) ||
                    formattedValue === '0.00'
                  )
                    error = t('errors.price.invalid');
                  if (isBiggerThanMaxPrice) error = t('errors.price.minBiggerThanMax');
                  if (isSameAsMaxPrice) error = t('errors.payPerUsage.samePrice');
                  return error;
                }}
                currencySymbol={currencySymbol}
                component={NumberInputWrapper}
                disabled={isPricesDisabled || isFormDisabled || countryIsPrefilled}
                required
                initialValue=''
              />
              <Field
                name={
                  copyPrices
                    ? `copyPricesMax${countryCurrency}`
                    : `allowedCountries[${countryIndex}].plans[0].usageMaxUnitPrice`
                }
                error={
                  (!dirtyFieldsSinceLastSubmit?.[
                    `allowedCountries[${countryIndex}].plans[0].usageMaxUnitPrice`
                  ] &&
                    submissionErrors?.[`plans[0].usageMaxUnitPrice`]) ||
                  errors?.allowedCountries?.[countryIndex]?.plans?.[0]?.usageMaxUnitPrice
                }
                label='pricing.payPerUsage.maxPrice.label'
                placeholder='pricing.payPerUsage.maxPrice.placeholder'
                validateFields={[
                  copyPrices
                    ? `copyPricesMin${countryCurrency}`
                    : `allowedCountries[${countryIndex}].plans[0].usageMinUnitPrice`,
                ]}
                changeValue={form?.change}
                pattern={priceWithMax2DecimalsRegex}
                validate={(value = '', allValues: { [key: string]: any }, fieldState) => {
                  const { visited } = fieldState ?? {};
                  if (!visited) return undefined;
                  const { allowedCountries, copyPrices } = allValues;
                  const selectedAllowedCountry = allowedCountries[countryIndex];
                  const payPerUsageMinPrice: number = copyPrices
                    ? allValues[`copyPricesMin${countryCurrency}`]
                    : selectedAllowedCountry?.plans?.[0]?.usageMinUnitPrice;
                  const formattedValue = `${value}`;
                  const numberFormattedValue = parseFloat(formattedValue);
                  const numberPayPerUsageMinPrice = parseFloat(`${payPerUsageMinPrice ?? 0}`);
                  const isLessThanMinPrice = numberFormattedValue < numberPayPerUsageMinPrice;
                  const isSameAsMinPrice = numberFormattedValue === numberPayPerUsageMinPrice;
                  let error; // no error
                  if (
                    !formattedValue.match(priceWithMax2DecimalsRegex) ||
                    formattedValue === '0.00'
                  )
                    error = t('errors.price.invalid');
                  if (isLessThanMinPrice) error = t('errors.price.maxLessThanMin');
                  if (isSameAsMinPrice) error = t('errors.payPerUsage.samePrice');
                  return error;
                }}
                currencySymbol={currencySymbol}
                component={NumberInputWrapper}
                disabled={isPricesDisabled || isFormDisabled || countryIsPrefilled}
                required
                initialValue=''
              />
            </div>
          )}
          <Note className='mt-1 mb-3'>
            {t('pricing.payPerUsage.priceBracket.hint', [
              {
                key: 'ENTITY',
                value: allowedCountry.unitDescription || 'global.entity',
                translateReplace: !allowedCountry.unitDescription,
              },
            ])}
          </Note>
        </>
      )}
      {isOnetime && (
        <>
          <Heading className={styles.heading} noTopPadding={countriesToPrefillFrom.length < 1}>
            {t('pricing.payPerUsage.pricingCommon.title')}
          </Heading>
          <Field
            name={`allowedCountries[${countryIndex}].activationFeePrice`}
            error={
              !dirtyFieldsSinceLastSubmit?.[
                `allowedCountries[${countryIndex}].activationFeePrice`
              ] && submissionErrors?.activationFeePrice
            }
            changeValue={form?.change}
            label='pricing.onetime.price.label'
            placeholder='pricing.onetime.price.placeholder'
            pattern={priceWithMax2DecimalsRegex}
            validate={(value = '', allValues, fieldState) => {
              const { visited } = fieldState ?? {};
              if (!visited) return undefined;
              const formattedValue = `${value}`;
              let isPriceValid = false;
              if (formattedValue.match(priceWithMax2DecimalsRegex) && formattedValue !== '0.00') {
                isPriceValid = true;
              }
              return isPriceValid ? undefined : t('errors.price.invalid');
            }}
            currencySymbol={currencySymbol}
            disabled={isFormDisabled || countryIsPrefilled}
            component={NumberInputWrapper}
            required
            initialValue=''
          />
          <Note className='mt-1 mb-3'>{t('pricing.onetime.priceBracket.hint')}</Note>
        </>
      )}
      {isMembership && (
        <>
          <Heading className={styles.heading} noTopPadding={countriesToPrefillFrom.length < 1}>
            {t('pricing.payPerUsage.pricingCommon.title')}
          </Heading>
          <Field
            // name={`plans[${planIndex ?? 0}].allowedCountries[${countryIndex}].monthlyPrice`} // in case a revert is needed where countries are in plans
            name={`plans[${planIndex ?? 0}].monthlyPrice`}
            error={
              (!dirtyFieldsSinceLastSubmit?.[`plans[${planIndex ?? 0}].monthlyPrice`] &&
                submissionErrors?.monthlyPrice) ||
              errors?.[planIndex ?? 0]?.monthlyPrice
            }
            changeValue={form?.change}
            label='pricing.membership.price.label'
            placeholder='pricing.membership.price.placeholder'
            pattern={priceWithMax2DecimalsRegex}
            validate={(value = '', allValues, fieldState) => {
              const { visited } = fieldState ?? {};
              if (!visited) return undefined;
              const formattedValue = `${value}`;
              let isPriceValid = false;
              if (formattedValue.match(priceWithMax2DecimalsRegex) && formattedValue !== '0.00') {
                isPriceValid = true;
              }
              return isPriceValid ? undefined : t('errors.price.invalid');
            }}
            currencySymbol={currencySymbol}
            component={NumberInputWrapper}
            disabled={isFormDisabled || countryIsPrefilled}
            required
            initialValue=''
          />
          <Note className='mt-1 mb-3'>{t('pricing.membership.priceBracket.hint')}</Note>
        </>
      )}
      {isMembership && (
        <>
          <Field
            name={`plans[${planIndex ?? 0}].description`}
            error={
              (!dirtyFieldsSinceLastSubmit?.[`plans[${planIndex ?? 0}].description`] &&
                submissionErrors?.description) ||
              errors?.[planIndex ?? 0]?.description
            }
            label={t('pricing.membership.planDescription.label')}
            validate={(value = '', allValues, fieldState) => {
              const { visited } = fieldState ?? {};
              if (!visited) return undefined;
              return !value.trim()?.length ? t('errors.required') : undefined;
            }}
            component={TextAreaWrapper}
            disabled={isFormDisabled || countryIsPrefilled}
            className='mb-1'
            required
          />
          <Note className={classNames('mt-1', { 'mb-2': !isOnetime })}>
            {t('pricing.membership.planDescription.hint')}
          </Note>
        </>
      )}
      {(isPayPerUsage || isOnetime) && (
        <>
          <Field
            name={`allowedCountries[${countryIndex}].priceDescription`}
            error={
              (!dirtyFieldsSinceLastSubmit?.[
                `allowedCountries[${countryIndex}].priceDescription`
              ] &&
                submissionErrors?.priceDescription) ||
              errors?.allowedCountries?.[countryIndex]?.priceDescription
            }
            label={t('pricing.payPerUsage.priceDescription.label')}
            validate={(value = '', allValues, fieldState) => {
              const { visited } = fieldState ?? {};
              if (!visited) return undefined;
              return !value.trim()?.length ? t('errors.required') : undefined;
            }}
            component={TextAreaWrapper}
            disabled={isFormDisabled || countryIsPrefilled}
            className='mb-1'
            required
          />
          <Note className={classNames('mt-1', { 'mb-2': !isOnetime })}>
            {t('pricing.payPerUsage.priceDescription.hint')}
          </Note>
        </>
      )}
      {(isPayPerUsage || isMembership) && (
        <>
          <Heading className={styles.heading}>{t('pricing.payPerUsage.oneTimeFee.title')}</Heading>
          <Field
            name={
              isMembership ? `plans[0].onetimeFee` : `allowedCountries[${countryIndex}].onetimeFee`
              // ? `plans[${planIndex ?? 0}].onetimeFee` // this is the correct value, but can't be used since we only save a country once and put all plans inside it. The value needs to be put on the country and not the plan, hence there's no way this can work
            }
            className='mb-2'
            label='pricing.payPerUsage.oneTimeFee.label'
            type='checkbox'
            disabled={isFormDisabled || countryIsPrefilled}
            component={SwitchInputWrapper}
          />
          {shouldRenderOnetimeFeeInput && (
            <div className={classNames(styles.box, 'mb-2')}>
              <Field
                label='pricing.payPerUsage.oneTimeFeeInput.label'
                placeholder='pricing.payPerUsage.oneTimeFeeInput.placeholder'
                name={
                  isMembership
                    ? `plans[0].activationFeePrice`
                    : `allowedCountries[${countryIndex}].activationFeePrice`
                  // ? `plans[${planIndex ?? 0}].activationFeePrice`
                }
                error={
                  (!dirtyFieldsSinceLastSubmit?.[
                    isMembership
                      ? `plans[0].activationFeePrice`
                      : `allowedCountries[${countryIndex}].activationFeePrice`
                  ] &&
                    submissionErrors?.activationFeePrice) ||
                  errors?.allowedCountries?.[countryIndex]?.activationFeePrice
                }
                changeValue={form?.change}
                validate={(value = '', allValues, fieldState) => {
                  const { dirty } = fieldState ?? {};
                  if (!dirty) return undefined;
                  const formattedValue = `${value}`;
                  let isPriceValid = false;
                  if (
                    formattedValue.match(priceWithMax2DecimalsRegex) &&
                    formattedValue !== '0.00'
                  ) {
                    isPriceValid = true;
                  }
                  return isPriceValid ? undefined : t('errors.price.invalid');
                }}
                className='mb-1'
                pattern={priceWithMax2DecimalsRegex}
                disabled={isFormDisabled || countryIsPrefilled}
                currencySymbol={currencySymbol}
                component={NumberInputWrapper}
                required
                initialValue=''
              />
              <Note className='mt-1'>{t('pricing.payPerUsage.oneTimeFee.hint')}</Note>
            </div>
          )}
        </>
      )}
      {isPayPerUsage && (
        <>
          <Heading className={styles.heading}>{t('pricing.entityCredits.title')}</Heading>
          <Field
            name={`allowedCountries[${countryIndex}].trialUnits`}
            label='pricing.entityCredits.label'
            pattern={floatNotNegativeNumberRegex}
            validate={(value = '', allValues, fieldState) => {
              const { dirty } = fieldState ?? {};
              if (!dirty) return undefined;
              const formattedValue = `${value}`;
              let isValid = false;
              if (formattedValue.match(floatNotNegativeNumberRegex)) {
                isValid = true;
              }
              return isValid ? undefined : t('errors.invalidValue');
            }}
            changeValue={form?.change}
            placeholder='pricing.entityCredits.placeholder'
            component={NumberInputWrapper}
            disabled={isFormDisabled || countryIsPrefilled}
            required
            initialValue=''
          />
          <Note className='mt-1 mb-3'>{t('pricing.entityCredits.hint')}</Note>
        </>
      )}
    </Accordion>
  );
};

export default AppPricingCountryForm;
